import { fmtDateTime, fmtTime, fmtWeekday, KIND_LABEL, mealName } from './format';
import type { Kind, LiveBoard, MenuItem, Notice, NoticeTopic, Pass, PassVenue, PortalEvent, Settings, Sitting, Venue } from './types';

const MIN = 60_000;
const KIND_ORDER: Record<Kind, number> = { veg: 0, nonveg: 1, common: 2 };

/** soon: opens within the hour. late: past opening, a delay notice is up and nobody has scanned yet. */
export type VenueStatus = 'soon' | 'late' | 'serving' | 'closing';

export interface LiveVenue {
  venue: Venue;
  kinds: Kind[];
  status: VenueStatus;
  /** First scan at this venue, when it came before or after the opening time. */
  since: string | null;
}

export interface LiveMeal {
  sitting: Sitting;
  status: VenueStatus;
  venues: LiveVenue[];
}

export interface LiveFeed {
  meals: LiveMeal[];
  notices: Notice[];
  /** The next meal after the ones on the board, within a week. */
  next: { sitting: Sitting; venues: LiveVenue[] } | null;
  /** Meals whose booking closes within 36 hours. */
  cutoffs: Sitting[];
  /** Something is happening now (a meal is serving or closing, or the committee posted). */
  live: boolean;
}

/** Venues where food is served (Where to eat, counters, backup lists), as opposed to places like Puja Prangan. */
export function foodVenues(venues: Venue[], menu: MenuItem[]): Venue[] {
  const used = new Set(menu.map((m) => m.venue_id));
  return venues.filter((v) => used.has(v.id));
}

export const TOPIC_LABEL: Record<NoticeTopic, string> = { food: 'Food', puja: 'Puja', culture: 'Cultural', general: 'Notice' };

/** "Shashti breakfast", "Ashtami Bhog lunch" (same wording as the notifications). */
export function mealTitle(s: Pick<Sitting, 'meal' | 'day_key' | 'day_label'>): string {
  const m = mealName(s);
  return `${s.day_label} ${m.startsWith('Bhog') ? m : m.toLowerCase()}`;
}

/** Venues of one meal with the kinds served at each, in kind order. */
export function mealVenues(sittingId: string, menu: MenuItem[], venues: Venue[]): { venue: Venue; kinds: Kind[] }[] {
  const byId = new Map(venues.map((v) => [v.id, v]));
  const out: { venue: Venue; kinds: Kind[] }[] = [];
  for (const it of menu.filter((m) => m.sitting_id === sittingId).sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind])) {
    const v = byId.get(it.venue_id);
    if (!v) continue;
    const row = out.find((x) => x.venue.id === v.id);
    if (row) row.kinds.push(it.kind);
    else out.push({ venue: v, kinds: [it.kind] });
  }
  return out;
}

/** Where each kind of a pass's meal is served: from the pass, or the menu for passes saved before venues. */
export function passVenues(pass: Pass, menu: MenuItem[] | undefined, venues: Venue[] | undefined): PassVenue[] {
  if (pass.venues?.length) return pass.venues;
  if (!menu || !venues) return [];
  return mealVenues(pass.sitting.id, menu, venues).flatMap((x) => x.kinds.map((kind) => ({ kind, id: x.venue.id, name: x.venue.name })));
}

const t = (iso: string) => new Date(iso).getTime();

export function buildFeed(input: {
  sittings: Sitting[];
  menu: MenuItem[];
  venues: Venue[];
  board: LiveBoard | undefined;
  settings: Settings | undefined;
  now: number;
}): LiveFeed {
  const { sittings, menu, venues, board, settings, now } = input;
  const lastCall = (settings?.last_call_minutes ?? 20) * MIN;
  // The programme of the events that are on, as event cards beside the committee's notices ("Sandhi Puja · 11:40 AM").
  const programme: Notice[] = (board?.programme ?? []).map((p) => ({
    id: `p-${p.id}`, body: p.title_bn ? `${p.title} · ${p.title_bn}` : p.title, tone: 'info', topic: p.topic, sitting_id: null,
    venue_id: p.venue_id, starts_at: p.starts_at, source: 'committee', at: p.starts_at,
    expires_at: p.ends_at ?? new Date(t(p.starts_at) + 60 * MIN).toISOString(),
  }));
  // Coming events first (soonest first), then the rest, newest first.
  const notices = [...(board?.notices ?? []), ...programme].filter((n) => t(n.expires_at) > now).sort((a, b) => {
    const ua = !!a.starts_at && t(a.starts_at) > now;
    const ub = !!b.starts_at && t(b.starts_at) > now;
    if (ua !== ub) return ua ? -1 : 1;
    return ua ? t(a.starts_at!) - t(b.starts_at!) : t(b.at) - t(a.at);
  });
  const delayed = (sid: string, vid: string) =>
    notices.some((n) => n.tone === 'delay' && n.sitting_id === sid && (!n.venue_id || n.venue_id === vid));

  const meals: LiveMeal[] = [];
  for (const s of sittings) {
    const opens = t(s.opens_at);
    const closes = t(s.closes_at);
    // A start only counts within the meal's own time (one left over from a rehearsal at another time doesn't).
    const starts = (board?.starts ?? []).filter((x) => x.sitting_id === s.id && t(x.at) >= opens - 2 * 60 * MIN && t(x.at) <= closes);
    const started = (vid: string) => starts.find((x) => x.venue_id === vid);
    const anyStart = starts.length > 0;
    if (now >= closes || (now < opens - 60 * MIN && !anyStart)) continue;
    const vs: LiveVenue[] = mealVenues(s.id, menu, venues).map(({ venue, kinds }) => {
      const st = started(venue.id);
      const status: VenueStatus =
        now >= closes - lastCall && (st || now >= opens) ? 'closing'
          : st || (now >= opens && !delayed(s.id, venue.id)) ? 'serving'
            : now >= opens ? 'late'
              : 'soon';
      return { venue, kinds, status, since: st?.via === 'scan' ? st.at : null };
    });
    if (!vs.length) continue;
    const rank: Record<VenueStatus, number> = { closing: 3, serving: 2, late: 1, soon: 0 };
    const status = vs.reduce<VenueStatus>((best, v) => (rank[v.status] > rank[best] ? v.status : best), 'soon');
    meals.push({ sitting: s, status, venues: vs });
  }

  const shown = new Set(meals.map((m) => m.sitting.id));
  const nextS = sittings.find((s) => !shown.has(s.id) && t(s.opens_at) > now && t(s.opens_at) - now < 7 * 24 * 60 * MIN);
  const cutoffs = settings?.booking_open === false ? [] : sittings
    .filter((s) => !shown.has(s.id) && t(s.opens_at) > now && t(s.booking_closes_at) > now && t(s.booking_closes_at) - now <= 36 * 60 * MIN)
    .slice(0, 2);

  return {
    meals,
    notices,
    next: nextS ? { sitting: nextS, venues: mealVenues(nextS.id, menu, venues).map((v) => ({ ...v, status: 'soon', since: null })) } : null,
    cutoffs,
    // Programme entries still to come don't make the board "live"; one under way does.
    live: notices.some((n) => typeof n.id === 'number' || t(n.starts_at!) <= now) || meals.some((m) => m.status !== 'soon'),
  };
}

export function mealHeadline(m: LiveMeal): string {
  const title = mealTitle(m.sitting);
  switch (m.status) {
    case 'serving': return `${title} is being served`;
    case 'closing': return `${title} closes at ${fmtTime(m.sitting.closes_at)}`;
    case 'late': return `${title} is running late`;
    default: return `${title} opens at ${fmtTime(m.sitting.opens_at)}`;
  }
}

export function venueStatusText(v: LiveVenue, s: Sitting): string {
  switch (v.status) {
    case 'serving': return v.since ? `Serving since ${fmtTime(v.since)}` : `Till ${fmtTime(s.closes_at)}`;
    case 'closing': return `Last call · closes ${fmtTime(s.closes_at)}`;
    case 'late': return 'Running late';
    default: return `Opens ${fmtTime(s.opens_at)}`;
  }
}

const lcFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

export const kindsText = (kinds: Kind[]) => kinds.map((k) => KIND_LABEL[k]).join(' & ');

/** Today: "8:00 AM"; another day: "16 Oct, 8:00 AM". */
export function whenText(iso: string, now: number): string {
  const day = (ms: number) => new Date(ms + 5.5 * 3600_000).toISOString().slice(0, 10);
  return day(t(iso)) === day(now) ? fmtTime(iso) : fmtDateTime(iso);
}

/** What a notice is about, when and where: the line under its text, and the start of its alert title. */
export function noticeContext(n: Pick<Notice, 'topic' | 'tone' | 'sitting_id' | 'venue_id' | 'starts_at'>, sittings: Sitting[], venues: Venue[], now: number) {
  const s = n.sitting_id ? sittings.find((x) => x.id === n.sitting_id) : undefined;
  const v = n.venue_id ? venues.find((x) => x.id === n.venue_id) : undefined;
  return {
    sitting: s,
    venue: v,
    about: s ? mealTitle(s) : TOPIC_LABEL[n.topic],
    when: n.starts_at ? whenText(n.starts_at, now) : null,
  };
}

/** The phone alert's title, as the server writes it (notice_title in the live_events migration). */
export function noticeAlertTitle(n: Pick<Notice, 'topic' | 'tone' | 'sitting_id' | 'venue_id' | 'starts_at'>, sittings: Sitting[], venues: Venue[], now: number): string {
  const c = noticeContext(n, sittings, venues, now);
  const head = c.sitting ? `${mealTitle(c.sitting)}${n.tone === 'delay' ? ' is running late' : ''}`
    : { food: 'Food update', puja: 'Puja', culture: 'Cultural programme', general: 'Announcement' }[n.topic];
  return [head, c.when, c.venue?.name].filter(Boolean).join(' · ');
}

/** Today for the banner: the Pujo day when there is one ("ষষ্ঠী", "Shashti"), the date and the time, in IST. */
export function today(sittings: Sitting[], now: number) {
  const iso = new Date(now).toISOString();
  const date = new Date(now + 5.5 * 3600_000).toISOString().slice(0, 10);
  const pujo = sittings.find((s) => s.serve_date === date);
  return { bn: pujo?.day_label_bn ?? null, day: pujo?.day_label ?? null, date: fmtWeekday(iso), time: fmtTime(iso), iso };
}

/** Mahalaya 2026 is Saturday 10 Oct. Its countdown runs to dawn, when Mahishasuramardini is on the radio. */
export const MAHALAYA_AT = '2026-10-10T04:00:00+05:30';

const DAY = 24 * 60 * MIN;
const istDate = (ms: number) => new Date(ms + 5.5 * 3600_000).toISOString().slice(0, 10);
const istMidnight = (date: string) => t(`${date}T00:00:00+05:30`);

export interface Countdown {
  /** mahalaya: counting to Mahalaya; mahalaya-day: Mahalaya is today; pujo: counting to the first Pujo day. */
  phase: 'mahalaya' | 'mahalaya-day' | 'pujo';
  /** What the big timer counts to. */
  target: string;
  /** Calendar days from today (IST): 0 is today. */
  mahalaya: { at: string; days: number };
  /** The first day's meal, for its Pujo day name; none when the event starts before its meals. */
  pujo: { at: string; days: number; day?: Sitting };
  /** The event being counted down to ("Durga Pujo 2026", "Diwali Celebration 2026"). */
  name: string;
}

/**
 * Before Pujo: the countdown to Mahalaya and to ONE10 Pujo (midnight of Shashti, the first Pujo day). Null from Shashti on.
 * The first day is the earliest of the live events' own start dates and their meals, so an event published before its
 * meals counts down too.
 */
export function pujoCountdown(sittings: Sitting[], now: number, events?: Map<string, Pick<PortalEvent, 'name'> & Partial<Pick<PortalEvent, 'status' | 'starts_on'>>>): Countdown | null {
  const starts = sittings.map((s) => ({ date: s.serve_date, eventId: s.event_id }));
  for (const [id, e] of events ?? []) if (e.status === 'live' && e.starts_on) starts.push({ date: e.starts_on, eventId: id });
  const first = starts.reduce<(typeof starts)[number] | null>((a, s) => (!a || s.date < a.date ? s : a), null);
  if (!first) return null;
  const pujo = istMidnight(first.date);
  if (now >= pujo) return null;
  const day = sittings.find((s) => s.serve_date === first.date && s.event_id === first.eventId);
  const mahalaya = t(MAHALAYA_AT);
  const mahalayaEnds = istMidnight(istDate(mahalaya)) + DAY;
  const days = (ms: number) => Math.round((istMidnight(istDate(ms)) - istMidnight(istDate(now))) / DAY);
  const phase = now < mahalaya ? 'mahalaya' : now < mahalayaEnds ? 'mahalaya-day' : 'pujo';
  const iso = (ms: number) => new Date(ms).toISOString();
  return {
    phase,
    target: iso(phase === 'mahalaya' ? mahalaya : pujo),
    mahalaya: { at: iso(mahalaya), days: days(mahalaya) },
    pujo: { at: iso(pujo), days: days(pujo), day },
    name: events?.get(first.eventId)?.name ?? 'ONE10 Pujo',
  };
}

/** "Today", "Tomorrow", "5 days". */
export function daysText(days: number): string {
  return days <= 0 ? 'Today' : days === 1 ? 'Tomorrow' : `${days} days`;
}

/** Days, hours, minutes and seconds left (zero once it's passed). */
export function timeLeft(iso: string, now: number) {
  const s = Math.max(0, Math.floor((t(iso) - now) / 1000));
  return { d: Math.floor(s / 86400), h: Math.floor(s / 3600) % 24, m: Math.floor(s / 60) % 60, s: s % 60 };
}

/** The last meal has closed: Bijoya. */
export function pujoOver(sittings: Sitting[], now: number): boolean {
  return sittings.length > 0 && sittings.every((s) => t(s.closes_at) <= now);
}

/** Places for the board: Puja Prangan and the like first, then the halls with what each serves. */
export function places(venues: Venue[], menu: MenuItem[]): { venue: Venue; kinds: Kind[] }[] {
  const out = venues.map((venue) => ({
    venue,
    kinds: [...new Set(menu.filter((m) => m.venue_id === venue.id).map((m) => m.kind))].sort((a, b) => KIND_ORDER[a] - KIND_ORDER[b]),
  }));
  return [...out.filter((p) => !p.kinds.length), ...out.filter((p) => p.kinds.length)];
}

/** "in 25 min", "in 2 h 10 min", "Now on", or the day for another date. */
export function startsIn(iso: string, now: number): string {
  const ms = t(iso) - now;
  if (ms <= 0) return 'Now on';
  const min = Math.round(ms / MIN);
  if (min < 60) return `in ${min} min`;
  if (min < 12 * 60) return `in ${Math.floor(min / 60)} h${min % 60 ? ` ${min % 60} min` : ''}`;
  return fmtDateTime(iso).split(',')[0];
}

const TOPIC_EMOJI: Record<NoticeTopic, string> = { food: '🍽️', puja: '🪔', culture: '🎶', general: '📣' };

/** The board as a WhatsApp message for the society groups. */
export function whatsappText(feed: LiveFeed, liveUrl: string, now: number, sittings: Sitting[] = [], venues: Venue[] = []): string {
  const out: string[] = [`*ONE10 food · Live* (${fmtTime(new Date(now).toISOString())})`];
  for (const m of feed.meals) {
    out.push('', `*${mealHeadline(m)}*`);
    for (const v of m.venues) out.push(`• ${kindsText(v.kinds)}: ${v.venue.name}, ${lcFirst(venueStatusText(v, m.sitting))}`);
  }
  for (const n of feed.notices) {
    const c = noticeContext(n, sittings, venues, now);
    out.push('', `${n.tone === 'delay' || n.tone === 'alert' ? '📣' : TOPIC_EMOJI[n.topic]} *${[c.about, c.when, c.venue?.name].filter(Boolean).join(' · ')}*`, n.body);
  }
  const cd = pujoCountdown(sittings, now);
  if (cd) {
    const pujo = `${cd.name} starts ${cd.pujo.days === 1 ? 'tomorrow' : `in ${daysText(cd.pujo.days)}`} (${cd.pujo.day ? `${cd.pujo.day.day_label}, ` : ''}${fmtWeekday(cd.pujo.at)})`;
    out.push('', cd.phase === 'mahalaya'
      ? `🪔 Mahalaya ${cd.mahalaya.days === 0 ? 'today' : cd.mahalaya.days === 1 ? 'tomorrow' : `in ${daysText(cd.mahalaya.days)}`} (${fmtWeekday(cd.mahalaya.at)}) · ${pujo}`
      : `🪔 ${cd.phase === 'mahalaya-day' ? 'Shubho Mahalaya! ' : ''}${pujo}`);
  }
  if (feed.next) {
    const s = feed.next.sitting;
    out.push('', `Next: ${mealTitle(s)}, ${whenText(s.opens_at, now)} (${feed.next.venues.map((v) => `${kindsText(v.kinds)}: ${v.venue.name}`).join(' · ')})`);
  }
  out.push('', `Live updates: ${liveUrl}`);
  return out.join('\n');
}
